import { ApolloError, useLazyQuery, useMutation } from '@apollo/client';
import {
	Button,
	Icon,
	SettingsGroup,
	SettingsItemAutocomplete,
	SettingsItemCheckbox,
	SettingsItemColor,
	SettingsItemTextField,
} from '@elipssolution/harfang';
import { mdiAlertCircle, mdiCheck, mdiPencil, mdiPlus } from '@mdi/js';
import { Typography, styled } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import SettingDomainTagNameField from './SettingDomainTagNameField';
import { useConfirmationDialogContext } from '../../../../../../components/ConfirmationDialogProvider';
import SettingsDialogPage from '../../../../../../components/SettingsDialogPage';
import { useSettingsDialog } from '../../../../../../hooks/useSettingsDialog';
import { DomainType } from '../../../../../../types/domain';
import { generateErrorInformations } from '../../../../../../utils/errorHandler';
import { SUCCESS_ANIMATION_DELAY } from '../../../../../../utils/statusAnimationDelay';
import {
	CREATE_SETTING_DOMAIN_TAG,
	CreateSettingDomainTagType,
	FETCH_SETTING_DOMAIN_TAG,
	FetchSettingDomainTagType,
	REMOVE_SETTING_DOMAIN_TAG,
	UPDATE_SETTING_DOMAIN_TAG,
	UpdateSettingDomainTagType,
} from '../../../../api/settingDomainTag';
import {
	FETCH_AUTOCOMPLETE_STORAGE_UPLOADS,
	FetchAutocompleteStorageUploadsType,
} from '../../../../api/settingStorageUpload';
import { DocumentConnectorCodeEnum } from '../../../../types/documentConnectorCode';
import { SettingStorageUploadType } from '../../../../types/settingStorage';
import { SettingTagByDomainType } from '../../../../types/settingTag';

const ErrorWrapper = styled('div')(({ theme }) => ({
	overflow: 'auto',

	minHeight: 100,
	minWidth: '50%',
	maxWidth: '75%',

	display: 'flex',
	flexDirection: 'column',
	justifyContent: 'center',
	alignItems: 'center',

	padding: theme.spacing(1),
	gap: theme.spacing(1),
	margin: '16px auto 0 auto',

	color: theme.palette.error.main,
	backgroundColor: `${theme.palette.error.main}1A`,
	borderRadius: theme.shape.borderRadius,
}));

const ButtonWrapper = styled('div')({
	display: 'flex',
	flexDirection: 'row-reverse',
	justifyContent: 'space-between',
});

type DomainTagsFormProps = {
	domain: DomainType;
	tag?: SettingTagByDomainType;
};

type FormFieldType = Pick<
	SettingTagByDomainType,
	'color' | 'isEnabled' | 'name' | 'storageUpload' | 'info' | 'configField'
>;

const DomainTagsForm = ({ domain, tag }: DomainTagsFormProps) => {
	const { id: domainId } = domain;
	const { id: initialTagId, name: initialTagName } = (tag || {}) as SettingTagByDomainType;

	const { back, onHasDirtyFormChange, onCloseConfirmationTitleChange } = useSettingsDialog();
	const { confirmAction } = useConfirmationDialogContext();

	const {
		clearErrors,
		control,
		formState: { isDirty, isValid },
		handleSubmit,
		setError,
		reset,
		watch,
	} = useForm<FormFieldType>();

	const [tagName, setTagName] = useState(initialTagName);
	const [tagId, setTagId] = useState(initialTagId);
	const [isTagArchived, setIsTagArchived] = useState(false);
	const [isCreateSettingDomainTagSucceeded, setIsCreateSettingDomainTagSucceeded] = useState(false);
	const [isUpdateSettingDomainTagSucceeded, setIsUpdateSettingDomainTagSucceeded] = useState(false);
	const [errorMessage, setErrorMessage] = useState('');

	const formStorageUpload = watch('storageUpload');
	const { connector, tagFieldLabel } = formStorageUpload ?? {};

	const [fetchSettingTag, { loading: isSettingDomainTagLoading }] = useLazyQuery<FetchSettingDomainTagType>(
		FETCH_SETTING_DOMAIN_TAG,
		{
			onCompleted: ({ document_settingDomainTag: { name } }) => setTagName(name),
			onError: (fetchError) =>
				setErrorMessage(
					generateErrorInformations({ error: fetchError, resource: 'document_settingDomainTag' }).message,
				),
		},
	);

	const [fetchSettingStorageUploads] = useLazyQuery<FetchAutocompleteStorageUploadsType>(
		FETCH_AUTOCOMPLETE_STORAGE_UPLOADS,
	);

	const settingStorageUploadsDataSource = useCallback(
		async (
			limit: number,
			offset: number,
			search?: string,
		): Promise<{
			items: Pick<SettingStorageUploadType, 'id' | 'name' | 'connector'>[];
			count: number;
		}> => {
			const { data, error } = await fetchSettingStorageUploads({
				variables: {
					page: {
						limit,
						offset,
					},
					search,
				},
			});

			if (error) {
				throw error;
			}

			const {
				document_settingStorageUploads: { count = 0, items = [] },
			} = data ?? {
				document_settingStorageUploads: {},
			};

			return {
				items,
				count,
			};
		},
		[fetchSettingStorageUploads],
	);

	const [createTag, { loading: isCreateTagLoading }] = useMutation<CreateSettingDomainTagType>(
		CREATE_SETTING_DOMAIN_TAG,
		{
			onCompleted: ({ document_createSettingDomainTag: { id } }) => {
				setTagId(id);
				setIsCreateSettingDomainTagSucceeded(true);
				setTimeout(() => setIsCreateSettingDomainTagSucceeded(false), SUCCESS_ANIMATION_DELAY);
			},
		},
	);

	const [updateTag, { loading: isUpdateTagLoading }] = useMutation<UpdateSettingDomainTagType>(
		UPDATE_SETTING_DOMAIN_TAG,
		{
			onCompleted: () => {
				setIsUpdateSettingDomainTagSucceeded(true);
				setTimeout(() => setIsUpdateSettingDomainTagSucceeded(false), SUCCESS_ANIMATION_DELAY);
			},
		},
	);

	const [removeTag, { loading: isRemoveTagLoading }] = useMutation(REMOVE_SETTING_DOMAIN_TAG, {
		onCompleted: back,
		onError: (error: ApolloError) =>
			setErrorMessage(generateErrorInformations({ error, resource: 'document_removeSettingDomainTag' })?.message),
	});

	const applyArchivedTag = useCallback(
		(tagArchivedId: string) => {
			setIsTagArchived(true);

			fetchSettingTag({
				variables: {
					settingDomainTagId: tagArchivedId,
				},
			}).catch((error) => {
				throw error;
			});
		},
		[fetchSettingTag],
	);

	const isLoading = useMemo(
		() => isSettingDomainTagLoading || isCreateTagLoading || isUpdateTagLoading,
		[isSettingDomainTagLoading, isCreateTagLoading, isUpdateTagLoading],
	);

	const isMutationButtonDisabled = useMemo(
		() => isRemoveTagLoading || !isValid || (!isDirty && !isTagArchived),
		[isDirty, isRemoveTagLoading, isTagArchived, isValid],
	);

	const mutationButtonName = useMemo(() => {
		if (isTagArchived) return 'Confirmer';
		if (tagId) {
			if (isCreateSettingDomainTagSucceeded) return 'Tag ajouté';
			if (isUpdateSettingDomainTagSucceeded) return 'Tag modifié';

			return 'Modifier';
		}

		return 'Ajouter';
	}, [isCreateSettingDomainTagSucceeded, isTagArchived, isUpdateSettingDomainTagSucceeded, tagId]);

	const startIcon = useMemo(
		() => (isTagArchived ? <Icon path={mdiCheck} /> : <Icon path={tagId ? mdiPencil : mdiPlus} />),
		[isTagArchived, tagId],
	);

	const onSubmit = useCallback(
		(values: FormFieldType) => {
			setErrorMessage('');

			const { color, isEnabled, name, storageUpload, info, configField } = values;
			const { id: storageUploadId } = storageUpload || {};

			const modeMutationInfoMap = new Map([
				[
					'create',
					{
						mutation: createTag,
						mutationName: 'createSettingDomainTag',
						inputValues: {
							domainId,
							color,
							isEnabled,
							name,
							storageUploadId,
							info,
							configField,
						},
					},
				],
				[
					'update',
					{
						mutation: updateTag,
						mutationName: 'updateSettingDomainTag',
						inputValues: {
							id: tagId,
							color,
							isEnabled,
							name,
							storageUploadId,
							...(isTagArchived && { isArchived: false }),
							configField,
							info: info || null,
						},
					},
				],
			]);

			const { mutation, mutationName = '', inputValues } = modeMutationInfoMap.get(tagId ? 'update' : 'create') ?? {};

			return mutation?.({
				variables: {
					[`${mutationName}Input`]: inputValues,
				},
			})
				.then(({ data }) => {
					if (!data) return;

					setIsTagArchived(false);

					reset(values);

					setTagName(name);
				})
				.catch((mutationError: ApolloError) =>
					setErrorMessage(generateErrorInformations({ error: mutationError, resource: mutationName }).message),
				);
		},
		[createTag, domainId, updateTag, tagId, isTagArchived, reset],
	);

	const handleRemoveButtonClick = async () => {
		if (!tagName) return undefined;

		const isRemovalConfirmed = await confirmAction({
			title: `Êtes-vous sûr de vouloir supprimer le tag ${tagName || ''} ?`,
			confirmButtonDetails: {
				color: 'error',
				label: 'Supprimer',
			},
		});

		if (!isRemovalConfirmed) return null;

		return removeTag({
			variables: {
				removeSettingDomainTagId: tagId,
			},
		});
	};

	useEffect(() => reset(tag), [reset, tag]);

	useEffect(() => {
		onHasDirtyFormChange(isDirty);
		onCloseConfirmationTitleChange(`Êtes-vous sûr de vouloir quitter la ${tag ? 'modification' : 'création'} du tag ?`);
	}, [tag, isDirty, onCloseConfirmationTitleChange, onHasDirtyFormChange]);

	return (
		<SettingsDialogPage title={tagName ?? "Ajout d'un tag"}>
			<SettingsGroup>
				<SettingDomainTagNameField
					clearErrors={clearErrors}
					control={control}
					domainId={domainId}
					initialValue={tagName}
					onApplyArchivedTag={applyArchivedTag}
					setError={setError}
					tagId={tagId}
				/>
				<Controller
					control={control}
					name="info"
					render={({ field: { onChange, ...field }, fieldState: { error } }) => (
						<SettingsItemTextField
							{...field}
							description="Informations du tag du domaine."
							helperText={error?.message ?? ' '}
							invalid={!!error}
							label="Information"
							onChange={onChange}
						/>
					)}
				/>

				<Controller
					name="color"
					control={control}
					render={({ field: { value, ...field } }) => (
						<SettingsItemColor
							{...field}
							label="Couleur"
							description="Couleur du tag."
							selectedColorCode={value}
							required
						/>
					)}
					rules={{ required: true }}
				/>
				<Controller
					name="storageUpload"
					control={control}
					render={({ field }) => (
						<SettingsItemAutocomplete<Pick<SettingStorageUploadType, 'id' | 'name' | 'connector'>>
							{...field}
							dataSource={settingStorageUploadsDataSource}
							description="Emplacement attaché au domaine."
							getOptionLabel={(option) => option?.name || ''}
							label="Emplacement"
							required
						/>
					)}
					rules={{ required: true }}
				/>
				<Controller
					name="isEnabled"
					control={control}
					defaultValue
					render={({ field: { value, ...field } }) => (
						<SettingsItemCheckbox
							{...field}
							checked={value}
							description="Si coché, le tag domaine est actif."
							disabled={isLoading}
							label="Actif"
						/>
					)}
				/>

				{(connector === DocumentConnectorCodeEnum.ACD || connector === DocumentConnectorCodeEnum.DOCUWARE) &&
					!!tagFieldLabel && (
						<Controller
							control={control}
							name="configField"
							render={({ field: { onChange, ...field } }) => (
								<SettingsItemTextField {...field} label={tagFieldLabel} onChange={(value) => onChange(value ?? null)} />
							)}
						/>
					)}
			</SettingsGroup>

			<ButtonWrapper>
				<Button
					disabled={isMutationButtonDisabled}
					onClick={handleSubmit(onSubmit)}
					startIcon={startIcon}
					variant="contained"
				>
					{mutationButtonName}
				</Button>

				{tagId && !isTagArchived && (
					<Button color="error" disabled={isLoading} onClick={handleRemoveButtonClick} variant="outlined">
						Supprimer
					</Button>
				)}
			</ButtonWrapper>

			{errorMessage && (
				<ErrorWrapper>
					<Icon path={mdiAlertCircle} />
					<Typography>{errorMessage}</Typography>
				</ErrorWrapper>
			)}
		</SettingsDialogPage>
	);
};

export default DomainTagsForm;
